// `RefreshConversationsButton` — Wave 3C (manual conversation sync).
//
// Renders the "Rafraîchir" button hosted in the DS `ConvList.headerActions`
// slot. Behaviour:
//
//   - Click → POST /api/v1/conversations/sync via `useSyncConversations`.
//   - 60 s client-side cooldown after a successful (or attempted) trigger
//     — too low a cooldown is dangerous because the alarm path auto-opens a
//     LinkedIn tab. Disable + tooltip "Réessayez dans Xs".
//   - Disable entirely while the targeted account is `limited` (or, in
//     "all accounts" view, while ANY visible account is `limited`).
//   - Server 429 surfaces in a small `Banner` rendered just below the
//     button (`Réessayez dans Xs` from `Retry-After` / `details.retryAfterMs`).
//   - Success → `useToast({ message: t('list.refreshSuccessToast') })` and
//     invalidate `['conversations']` so the list re-fetches.
//   - In-flight → DS `Button` shows its built-in spinner via `loading` prop.
//
// Coordination:
//   - Wave 3A wires the extension alarm + `conversations.sync-requested`
//     listener.
//   - Wave 3B owns the API endpoint + rate-limit bucket. Tests mock the
//     api-client so no live API dependency.

import { useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  ApiError,
  invalidateConversations,
  useSyncConversations,
  type LinkedinAccountRow,
} from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Banner, Button, useToast } from '@barel/ui';
import { useQueryClient } from '@tanstack/react-query';

const COOLDOWN_MS = 60_000;

export interface RefreshConversationsButtonProps {
  accounts: LinkedinAccountRow[];
  selectedAccountId: string | 'all';
}

interface RateLimitState {
  /** Wall-clock timestamp (ms) when the user can retry. */
  retryAt: number;
}

/** Pull a sensible "retry in N seconds" hint out of an ApiError 429 envelope. */
function extractRetryAfterMs(err: unknown): number | null {
  if (!(err instanceof ApiError) || err.httpStatus !== 429) return null;
  const details = err.details as { retryAfterMs?: number; retryAfter?: number } | undefined;
  if (typeof details?.retryAfterMs === 'number' && Number.isFinite(details.retryAfterMs)) {
    return details.retryAfterMs;
  }
  if (typeof details?.retryAfter === 'number' && Number.isFinite(details.retryAfter)) {
    return details.retryAfter * 1000;
  }
  // Default: align with the 60 s client cooldown.
  return COOLDOWN_MS;
}

export function RefreshConversationsButton(props: RefreshConversationsButtonProps): ReactNode {
  const t = useT('inbox');
  const toast = useToast();
  const qc = useQueryClient();
  const sync = useSyncConversations();

  const [lastClickAt, setLastClickAt] = useState<number | null>(null);
  const [rateLimited, setRateLimited] = useState<RateLimitState | null>(null);
  // Tick state purely so the cooldown countdown re-renders every second.
  const [, setTick] = useState(0);

  const isLimited = useMemo(() => {
    if (props.selectedAccountId !== 'all') {
      const acc = props.accounts.find((a) => a.id === props.selectedAccountId);
      return acc?.status === 'limited';
    }
    return props.accounts.some((a) => a.status === 'limited');
  }, [props.accounts, props.selectedAccountId]);

  const cooldownRemainingMs = lastClickAt ? COOLDOWN_MS - (Date.now() - lastClickAt) : 0;
  const inCooldown = cooldownRemainingMs > 0;
  const rateLimitRemainingMs = rateLimited ? rateLimited.retryAt - Date.now() : 0;
  const inRateLimit = rateLimitRemainingMs > 0;

  // Re-render every second while either timer is active so the tooltip
  // / banner countdown stays accurate.
  useEffect(() => {
    if (!inCooldown && !inRateLimit) return;
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [inCooldown, inRateLimit]);

  // Drop the rate-limit banner once the window expires.
  useEffect(() => {
    if (!rateLimited) return;
    if (rateLimited.retryAt <= Date.now()) {
      setRateLimited(null);
    }
  }, [rateLimited, rateLimitRemainingMs]);

  const isPending = sync.isPending;
  const disabled = isLimited || inCooldown || inRateLimit || isPending;

  const tooltip = useMemo(() => {
    if (isLimited) return t('list.refreshTooltipLimited');
    if (inRateLimit) {
      return t('list.refreshTooltipCooldown', {
        seconds: Math.max(1, Math.ceil(rateLimitRemainingMs / 1000)),
      });
    }
    if (inCooldown) {
      return t('list.refreshTooltipCooldown', {
        seconds: Math.max(1, Math.ceil(cooldownRemainingMs / 1000)),
      });
    }
    return t('list.refreshTooltipReady');
  }, [isLimited, inRateLimit, inCooldown, rateLimitRemainingMs, cooldownRemainingMs, t]);

  const onClick = () => {
    if (disabled) return;
    setLastClickAt(Date.now());
    sync.mutate(
      {
        accountId: props.selectedAccountId === 'all' ? undefined : props.selectedAccountId,
      },
      {
        onSuccess: () => {
          toast.success({ title: t('list.refreshSuccessToast') });
          invalidateConversations(qc);
        },
        onError: (err) => {
          const retryAfterMs = extractRetryAfterMs(err);
          if (retryAfterMs !== null) {
            setRateLimited({ retryAt: Date.now() + retryAfterMs });
            return;
          }
          toast.error({ title: t('list.refreshErrorToast') });
        },
      },
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      <Button
        variant="ghost"
        size="sm"
        onClick={onClick}
        disabled={disabled}
        loading={isPending}
        title={tooltip}
        aria-label={t('list.refreshAria')}
        data-testid="inbox-refresh-button"
        data-cooldown={inCooldown ? 'true' : undefined}
        data-limited={isLimited ? 'true' : undefined}
      >
        {t('list.refreshButton')}
      </Button>
      {inRateLimit ? (
        <Banner
          tone="warning"
          data-testid="inbox-refresh-rate-limit"
          title={t('list.refreshRateLimitedTitle')}
        >
          {t('list.refreshRateLimitedBody', {
            seconds: Math.max(1, Math.ceil(rateLimitRemainingMs / 1000)),
          })}
        </Banner>
      ) : null}
    </div>
  );
}
